import { api } from "../api";
import type { MailNotificationsResponse, MailTriggerInfo, MailTriggerState } from "../types";
import { esc } from "../utils/html";

/**
 * Einstellungen → Benachrichtigungen: pro Schule steuern, worüber Schüler per
 * E-Mail informiert werden, und den Text dazu anpassen. Katalog und Standardtexte
 * kommen aus dem Backend (backend/src/services/mail-templates.ts).
 *
 * Der Trigger "Zugang eingerichtet" hat keinen eigenen Text: diese Mail verschickt
 * der Anmeldedienst, der Wortlaut gilt deshalb für alle Schulen gleich.
 */

function showToast(message: string) {
  const toast = document.createElement("div");
  toast.className = "toast";
  toast.textContent = message;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add("toast--visible"));
  setTimeout(() => {
    toast.classList.remove("toast--visible");
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}

/** Vorschau: dieselbe Aufbereitung wie im Backend, nur zur Anzeige. */
function preview(body: string): string {
  const filled = body
    .replace(/\{\{vorname\}\}/g, "Lena")
    .replace(/\{\{nachname\}\}/g, "Musterfrau")
    .replace(/\{\{schule\}\}/g, "deine Schule");
  return esc(filled).replace(/\n/g, "<br>");
}

/**
 * Welche Text-Editoren aufgeklappt sind. Nach dem Speichern wird die Seite neu
 * gezeichnet; ohne diesen Merker würde der Editor dabei zuklappen.
 */
const openEditors = new Set<string>();

function renderCard(
  trigger: MailTriggerInfo,
  state: MailTriggerState,
  limits: { subject: number; body: number },
  placeholders: string[]
): string {
  const toggle = `
    <label style="display:flex;align-items:flex-start;gap:.75rem;cursor:pointer">
      <input type="checkbox" class="mail-trigger-toggle" data-key="${esc(trigger.key)}" ${state.enabled ? "checked" : ""}
        style="width:16px;height:16px;margin-top:2px;accent-color:var(--primary);flex-shrink:0" />
      <div>
        <div style="font-weight:500;font-size:.875rem">${esc(trigger.label)}</div>
        <div style="font-size:.8125rem;color:var(--gray-500);margin-top:.25rem">${esc(trigger.description)}</div>
      </div>
    </label>`;

  if (!trigger.editable) {
    return `
      <div class="card" style="padding:1.25rem">
        ${toggle}
        <div style="margin:.5rem 0 0 2.25rem;font-size:.75rem;color:var(--gray-500)">
          Text vom Anmeldedienst, für alle Schulen gleich: „${esc(trigger.defaultSubject)}“
        </div>
      </div>`;
  }

  // Die Anlässe stehen offen da, der Text liegt dahinter in einer Aufklapp-Zeile:
  // Anlässe wählt man beim Einrichten, den Text ändert man selten.
  const events = (trigger.events ?? []).map(e => `
    <label style="display:flex;align-items:flex-start;gap:.5rem;cursor:pointer;margin-bottom:.5rem">
      <input type="checkbox" class="mail-event-toggle" data-key="${esc(trigger.key)}"
        data-event="${esc(e.key)}" ${state.events?.[e.key] !== false ? "checked" : ""}
        style="width:14px;height:14px;margin-top:2px;accent-color:var(--primary);flex-shrink:0" />
      <div>
        <div style="font-size:.8125rem">${esc(e.label)}</div>
        <div style="font-size:.75rem;color:var(--gray-500)">${esc(e.description)}</div>
      </div>
    </label>`).join("");

  return `
    <div class="card" style="padding:1.25rem">
      ${toggle}
      <div style="margin:.5rem 0 0 2.25rem;display:${state.enabled ? "block" : "none"}" data-editor="${esc(trigger.key)}">
        ${events ? `<div style="padding:.75rem;background:var(--gray-50);border-radius:.5rem;margin-bottom:.75rem">
          <div style="font-size:.75rem;color:var(--gray-500);margin-bottom:.5rem">
            Wobei soll eine Nachricht rausgehen? Fallen mehrere Anlässe zusammen, bleibt es bei einer Mail.
          </div>
          ${events}
        </div>` : ""}
        <details class="mail-details" data-key="${esc(trigger.key)}"${openEditors.has(trigger.key) ? " open" : ""}>
          <summary style="cursor:pointer;font-size:.8125rem;color:var(--primary);list-style:revert">
            Text anpassen
            <span style="color:var(--gray-500)">
              (${state.isCustom ? "eigener Text" : "Standardtext"})
            </span>
          </summary>
          <div style="margin-top:.75rem">
        <label style="display:block;font-size:.8125rem;font-weight:500;margin-bottom:.25rem">Betreff</label>
        <input type="text" class="mail-subject" data-key="${esc(trigger.key)}" maxlength="${limits.subject}"
          value="${esc(state.subject)}" style="width:100%;padding:.5rem;border:1px solid var(--gray-300);border-radius:.375rem;font-size:.875rem" />

        <label style="display:block;font-size:.8125rem;font-weight:500;margin:.75rem 0 .25rem">Text</label>
        <textarea class="mail-body" data-key="${esc(trigger.key)}" rows="7" maxlength="${limits.body}"
          style="width:100%;padding:.5rem;border:1px solid var(--gray-300);border-radius:.375rem;font-size:.875rem;font-family:inherit;resize:vertical">${esc(state.body)}</textarea>
        <div style="font-size:.75rem;color:var(--gray-500);margin-top:.25rem">
          <span class="mail-counter" data-key="${esc(trigger.key)}">${state.body.length}</span> / ${limits.body} Zeichen
        </div>

        <div style="margin-top:.5rem;display:flex;flex-wrap:wrap;gap:.375rem;align-items:center">
          <span style="font-size:.75rem;color:var(--gray-500)">Platzhalter einfügen:</span>
          ${placeholders.map(p => `
            <button type="button" class="mail-placeholder" data-key="${esc(trigger.key)}" data-placeholder="{{${esc(p)}}}"
              style="font-size:.75rem;padding:.125rem .5rem;border:1px solid var(--gray-300);border-radius:1rem;background:var(--gray-50);cursor:pointer">{{${esc(p)}}}</button>`).join("")}
        </div>

        <div style="margin-top:.75rem;padding:.75rem;background:var(--gray-50);border-radius:.5rem">
          <div style="font-size:.75rem;color:var(--gray-500);margin-bottom:.375rem">Vorschau</div>
          <div style="font-size:.8125rem;color:var(--gray-700)" class="mail-preview" data-key="${esc(trigger.key)}">${preview(state.body)}</div>
          <div style="font-size:.75rem;color:var(--gray-500);margin-top:.75rem;padding-top:.5rem;border-top:1px solid var(--gray-200)">
            Link zum Schüler-Portal<br>
            Diese Nachricht wurde automatisch im Auftrag deiner Schule versendet.
            Bei Fragen wende dich bitte an deine Schule.
          </div>
        </div>

        <div style="margin-top:.75rem;display:flex;gap:.5rem;flex-wrap:wrap">
          <button class="btn btn-primary mail-save" data-key="${esc(trigger.key)}" style="font-size:.8125rem">Speichern</button>
          <button class="btn mail-reset" data-key="${esc(trigger.key)}" style="font-size:.8125rem"${state.isCustom ? "" : " disabled"}>Auf Standard zurücksetzen</button>
          <button class="btn mail-test" data-key="${esc(trigger.key)}" style="font-size:.8125rem"
            title="Speichert den Text und schickt ihn an deine Adresse">Testmail an mich</button>
        </div>
          </div>
        </details>
      </div>
    </div>`;
}

const PAGE_HEADER = `
  <div class="settings-page-header">
    <div>
      <h1>Benachrichtigungen</h1>
      <p>E-Mails, mit denen Meduvio deine Schüler über Neuigkeiten in ihrem Portal informiert.</p>
    </div>
  </div>`;

export async function render(el: HTMLElement) {
  let data: MailNotificationsResponse;
  try {
    data = await api.settings.mailNotifications.get();
  } catch (err: any) {
    el.innerHTML = `${PAGE_HEADER}<div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
    return;
  }

  // Ohne aktives Schüler-Portal versendet nichts davon etwas: das Einreihen prüft
  // dieselbe Bedingung, und das Anlegen eines Schüler-Logins wird ohnehin abgelehnt.
  // Schalter anzubieten, die nachweislich wirkungslos sind, wäre irreführend.
  if (!data.schuelerPortalEnabled) {
    el.innerHTML = `
      ${PAGE_HEADER}
      <div class="card" style="padding:1.5rem">
        <p style="font-size:.875rem;color:var(--gray-600);margin:0 0 1rem">
          Diese Nachrichten gehen an Schüler in ihrem Schüler-Portal. Solange das Portal für
          deine Schule nicht aktiviert ist, gibt es keine Empfänger und es wird nichts versendet.
          Sobald es freigeschaltet ist, kannst du hier einstellen, worüber deine Schüler
          per E-Mail informiert werden.
        </p>
        <a class="btn btn-primary" href="#/abonnements">Zu den Abonnements</a>
      </div>`;
    return;
  }

  const renderBody = () => {
    el.innerHTML = `
      ${PAGE_HEADER}
      <div style="display:flex;flex-direction:column;gap:1rem">
        <div class="alert" style="background:var(--gray-50);border:1px solid var(--gray-200);font-size:.8125rem;color:var(--gray-700)">
          Die Inhalte dieser Nachrichten verantwortet deine Einrichtung als datenschutzrechtlich
          Verantwortliche, Meduvio versendet sie in deinem Auftrag. Bitte nimm keine
          personenbezogenen Details wie Namen von Praktikumsstellen, Dokumentnamen oder Termine
          in den Text auf und füge keine Links ein. Der Link zum Schüler-Portal wird automatisch angehängt.
        </div>
        ${data.triggers.map(t => renderCard(t, data.settings[t.key], data.limits, data.placeholders)).join("")}
      </div>`;

    const editorOf = (key: string) => el.querySelector<HTMLElement>(`[data-editor="${key}"]`);
    const subjectOf = (key: string) => el.querySelector<HTMLInputElement>(`.mail-subject[data-key="${key}"]`);
    const bodyOf = (key: string) => el.querySelector<HTMLTextAreaElement>(`.mail-body[data-key="${key}"]`);

    el.querySelectorAll<HTMLInputElement>(".mail-trigger-toggle").forEach(cb => {
      cb.addEventListener("change", async () => {
        const key = cb.dataset.key!;
        const enabled = cb.checked;
        cb.disabled = true;
        try {
          const result = await api.settings.mailNotifications.update(key, { enabled });
          data.settings = result.settings;
          const label = data.triggers.find(t => t.key === key)?.label ?? key;
          showToast(`"${label}" ${enabled ? "aktiviert" : "deaktiviert"}`);
          const editor = editorOf(key);
          if (editor) editor.style.display = enabled ? "block" : "none";
        } catch (err: any) {
          cb.checked = !enabled;
          showToast("Fehler: " + err.message);
        } finally {
          cb.disabled = false;
        }
      });
    });

    el.querySelectorAll<HTMLInputElement>(".mail-event-toggle").forEach(cb => {
      cb.addEventListener("change", async () => {
        const key = cb.dataset.key!;
        const event = cb.dataset.event!;
        const enabled = cb.checked;
        cb.disabled = true;
        try {
          const result = await api.settings.mailNotifications.update(key, {
            events: { [event]: enabled },
          });
          data.settings = result.settings;
          showToast(enabled ? "Anlass aktiviert" : "Anlass deaktiviert");
        } catch (err: any) {
          cb.checked = !enabled;
          showToast("Fehler: " + err.message);
        } finally {
          cb.disabled = false;
        }
      });
    });

    el.querySelectorAll<HTMLDetailsElement>(".mail-details").forEach(d => {
      d.addEventListener("toggle", () => {
        const key = d.dataset.key!;
        if (d.open) openEditors.add(key); else openEditors.delete(key);
      });
    });

    // Live-Vorschau und Zeichenzähler
    el.querySelectorAll<HTMLTextAreaElement>(".mail-body").forEach(ta => {
      ta.addEventListener("input", () => {
        const key = ta.dataset.key!;
        const target = el.querySelector<HTMLElement>(`.mail-preview[data-key="${key}"]`);
        if (target) target.innerHTML = preview(ta.value);
        const counter = el.querySelector<HTMLElement>(`.mail-counter[data-key="${key}"]`);
        if (counter) counter.textContent = String(ta.value.length);
      });
    });

    el.querySelectorAll<HTMLButtonElement>(".mail-placeholder").forEach(btn => {
      btn.addEventListener("click", () => {
        const ta = bodyOf(btn.dataset.key!);
        if (!ta) return;
        const at = ta.selectionStart ?? ta.value.length;
        const marker = btn.dataset.placeholder!;
        ta.value = ta.value.slice(0, at) + marker + ta.value.slice(ta.selectionEnd ?? at);
        ta.focus();
        ta.selectionStart = ta.selectionEnd = at + marker.length;
        ta.dispatchEvent(new Event("input"));
      });
    });

    el.querySelectorAll<HTMLButtonElement>(".mail-save").forEach(btn => {
      btn.addEventListener("click", async () => {
        const key = btn.dataset.key!;
        const subject = subjectOf(key)?.value ?? "";
        const body = bodyOf(key)?.value ?? "";
        btn.disabled = true;
        try {
          const result = await api.settings.mailNotifications.update(key, { subject, body });
          data.settings = result.settings;
          showToast("Text gespeichert");
          renderBody();
        } catch (err: any) {
          showToast("Fehler: " + err.message);
          btn.disabled = false;
        }
      });
    });

    el.querySelectorAll<HTMLButtonElement>(".mail-reset").forEach(btn => {
      btn.addEventListener("click", async () => {
        const key = btn.dataset.key!;
        btn.disabled = true;
        try {
          const result = await api.settings.mailNotifications.resetText(key);
          data.settings = result.settings;
          showToast("Standardtext wiederhergestellt");
          renderBody();
        } catch (err: any) {
          showToast("Fehler: " + err.message);
          btn.disabled = false;
        }
      });
    });

    el.querySelectorAll<HTMLButtonElement>(".mail-test").forEach(btn => {
      btn.addEventListener("click", async () => {
        const key = btn.dataset.key!;
        btn.disabled = true;
        try {
          // Erst speichern: die Testmail rendert serverseitig den gespeicherten Stand.
          // Ohne das bekäme man den alten Text zu sehen und würde am Entwurf vorbeitesten.
          // Scheitert die Prüfung, etwa an einem Link im Text, meldet schon dieser Schritt.
          const saved = await api.settings.mailNotifications.update(key, {
            subject: subjectOf(key)?.value ?? "",
            body: bodyOf(key)?.value ?? "",
          });
          data.settings = saved.settings;
          const result = await api.settings.mailNotifications.sendTest(key);
          showToast(`Testmail an ${result.recipient} verschickt`);
          // Neu zeichnen, damit "Auf Standard zurücksetzen" den neuen Zustand kennt.
          renderBody();
        } catch (err: any) {
          showToast("Fehler: " + err.message);
          btn.disabled = false;
        }
      });
    });
  };

  renderBody();
}
